import { useCallback, useEffect, useRef, useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import { Select, Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import type { SyncFilterDefinition } from 'modules/integrations/lib/syncFilters'
import type { Parameters } from 'modules/integrations/lib/parameters'

interface SyncFiltersModalProps {
  syncFilters: SyncFilterDefinition[]
  skipConfirmationIfNoIssues?: boolean
  onClose: () => void
  onContinue: () => void
}

export const SyncFiltersModal = ({
  syncFilters,
  skipConfirmationIfNoIssues = false,
  onClose,
  onContinue,
}: SyncFiltersModalProps) => {
  const { setFilterParameters, capabilityMode, configuration } = usePrivateIntegrationsSyncContext()

  const [values, setValues] = useState<Record<string, string[]>>({})
  const hasInitialized = useRef(false)

  // Initialize default values when filters finish loading
  useEffect(() => {
    if (hasInitialized.current || syncFilters.length === 0) return
    const allLoaded = syncFilters.every((f) => !f.loading)
    if (!allLoaded) return

    const initial: Record<string, string[]> = {}
    for (const filter of syncFilters) {
      initial[filter.key] = filter.selectAllByDefault ? filter.options.map((o) => o.value) : []
    }
    setValues(initial)
    hasInitialized.current = true
  }, [syncFilters])

  const handleChange = useCallback((key: string, selected: string[]) => {
    setValues((prev) => ({ ...prev, [key]: selected }))
  }, [])

  const isAnyLoading = syncFilters.some((f) => f.loading)

  const isValid = syncFilters.every((filter) => {
    if (!filter.required) return true
    const selected = values[filter.key] ?? []
    return selected.length > 0
  })

  const handleContinue = () => {
    const parameters: Parameters = {}
    for (const filter of syncFilters) {
      const selected = values[filter.key] ?? []
      if (selected.length > 0) {
        parameters[filter.key] = selected.join(',')
      }
    }
    setFilterParameters(parameters)
    onContinue()
  }

  const isExportMode = capabilityMode === 'file'
  const entityName = configuration.syncableEntityName()
  const title = isExportMode
    ? i18n.t('integrations.sync_filters_modal.export_title', { entity_name: entityName })
    : i18n.t('integrations.sync_filters_modal.sync_title', { entity_name: entityName })

  const primaryLabel = skipConfirmationIfNoIssues
    ? isExportMode
      ? i18n.t('integrations.confirmation_modal.export_cta')
      : i18n.t('integrations.confirmation_modal.sync_cta')
    : i18n.t('common.continue')

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={title}
      primaryAction={{
        label: primaryLabel,
        onClick: handleContinue,
        disabled: isAnyLoading || !isValid,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Stack gap="4">
        {syncFilters.map((filter) => (
          <Select
            key={filter.key}
            clearable
            loading={filter.loading}
            label={filter.label}
            name={filter.key}
            placeholder={i18n.t('common.select')}
            options={filter.options}
            showSearchBox={filter.searchable}
            size="md"
            multiple
            value={values[filter.key] ?? []}
            onChange={(value: string[]) => handleChange(filter.key, value)}
            required={filter.required}
          />
        ))}
      </Stack>
    </F0Dialog>
  )
}
